import IconBot from '@shared/icons/IconBot';
import { Link, useLocation, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ReactSortable } from 'react-sortablejs';

import type {
  ConversationFolderItem,
  ConversationItem as ConversationItemType,
  GetConversationFoldersResponse,
  GetConversationsResponse,
} from '@/api/hooks/aiAdvisoryQueryActions';
import { useAiAdvisoryQueryActions } from '@/api/hooks/aiAdvisoryQueryActions';
import IconCaretDown from '@/assets/Icons/IconCaretDown';
import IconFolder from '@/assets/Icons/IconFolder';
import IconFolderPlus from '@/assets/Icons/IconFolderPlus';
import IconPencil from '@/assets/Icons/IconPencil';
import IconPlusCircle from '@/assets/Icons/IconPlusCircle';
import IconTrash from '@/assets/Icons/IconTrash';
import { ConfirmationModal } from '@/components/molecules/ConfirmationModal';
import { messages as SidebarMessages } from '@/components/organism/Sidebar/messages.ts';
import { useMessages } from '@/hooks/messages';
import { aiAdvisoryMessages } from '@/pages/aiAdvisory/messages';
import { EstimationNavigationItem } from '@/pages/estimation/components/estimationNavigation/estimationNavigationItem';

import { groupConversationsByFolder } from './conversationGrouping';
import { FolderPicker } from './FolderPicker';
import { InlineRenameInput } from './InlineRenameInput';
import { useExpandedFoldersState } from './useExpandedFoldersState';

const MAX_FOLDER_NAME_LENGTH = 60;
const MAX_CONVERSATION_TITLE_LENGTH = 100; // Matches backend RenameConversationRequest constraint (EWM-520).
const MAX_TITLE_DISPLAY_LENGTH = 35;

// EWM-521 §5.5: one shared SortableJS group so a conversation can be dragged between any folder
// list and the unfoldered list. pull + put both enabled for free movement in either direction.
const CONVERSATION_SORTABLE_GROUP = {
  name: 'ai-advisory-conversations',
  pull: true,
  put: true,
};

interface IAiAdvisorySidebarProps {
  isSidebarOpen: boolean;
  onItemClick?: () => void;
}

interface IConversationDisplay {
  /** EWM-521 §5.5: ReactSortable requires an `id` field on list items; mirrors conversationId. */
  id: string;
  conversationId: string;
  rawTitle: string;
  displayTitle: string;
  folderId: string | null;
  isActive: boolean;
  navigateTo: string;
  /** EWM-534: false when shared with the current user by someone else — owner-only actions are hidden. */
  isOwner: boolean;
}

/**
 * Cleans up a conversation title for display in the sidebar — falls back to a date-based
 * label for empty / repetitive auto-generated titles, and truncates at a word boundary.
 * Mirrors the existing dedupe + clean-up rules so the folder refactor doesn't regress them.
 */
const buildDisplayTitle = (raw: string, createdAtIso: string): string => {
  let title = raw.trim();
  const hasRepetitivePattern = /\b(\w+)\s+\1\b/i.test(title);
  if (title.length === 0 || hasRepetitivePattern) {
    const date = new Date(createdAtIso).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
    title = `Conversation - ${date}`;
  }
  title = title.replace(/\s+/g, ' ').trim();
  if (title.length > MAX_TITLE_DISPLAY_LENGTH) {
    const truncated = title.substring(0, MAX_TITLE_DISPLAY_LENGTH);
    const lastSpace = truncated.lastIndexOf(' ');
    if (lastSpace > MAX_TITLE_DISPLAY_LENGTH * 0.7) {
      title = truncated.substring(0, lastSpace) + '...';
    } else {
      title = truncated + '...';
    }
  }
  return title;
};

interface IConversationRowProps {
  conversation: IConversationDisplay;
  index: number;
  isSidebarOpen: boolean;
  /** EWM-521 §5.1: conversations nested inside a folder render one indent level deeper. */
  isInFolder: boolean;
  folders: ConversationFolderItem[];
  isPickerOpen: boolean;
  isEditing: boolean;
  onItemClick?: () => void;
  onOpenPicker: () => void;
  onClosePicker: () => void;
  onMoveToFolder: (folderId: string | null) => Promise<void>;
  onCreateFolderAndMove: (name: string) => Promise<void>;
  onDelete: () => void;
  onEnterEditMode: () => void;
  onCancelEdit: () => void;
  onRename: (newTitle: string) => Promise<void>;
  /** Localized strings — passed in to keep this component pure. */
  labels: {
    moveToFolder: string;
    deleteConversation: string;
    moveToFolderNoFolderOption: string;
    moveToFolderCreateNew: string;
    renameFolder: string;
    renameFolderConfirm: string;
    renameFolderCancel: string;
    renameConversation: string;
    renameConversationConfirm: string;
    renameConversationCancel: string;
    sharedBadge: string;
    sharedBadgeTitle: string;
  };
}

const ConversationRow: React.FC<IConversationRowProps> = ({
  conversation,
  index,
  isSidebarOpen,
  isInFolder,
  folders,
  isPickerOpen,
  isEditing,
  onItemClick,
  onOpenPicker,
  onClosePicker,
  onMoveToFolder,
  onCreateFolderAndMove,
  onDelete,
  onEnterEditMode,
  onCancelEdit,
  onRename,
  labels,
}) => {
  // EWM-936: held as state via a callback ref rather than useRef, because the element is
  // read during render to anchor the folder picker. Reading ref.current while rendering is
  // not allowed; a callback ref re-renders with the node, so the anchor is always current.
  const [folderButtonEl, setFolderButtonEl] = useState<HTMLButtonElement | null>(null);

  // EWM-521 §5.1: nest foldered conversations one level deeper than unfoldered ones
  // so the sidebar hierarchy reads clearly under each folder header.
  const indentClass = isInFolder ? 'pl-12' : 'pl-8';

  if (isEditing) {
    return (
      <div className={`group/item relative ${indentClass}`}>
        <InlineRenameInput
          initialValue={conversation.rawTitle}
          maxLength={MAX_CONVERSATION_TITLE_LENGTH}
          saveLabel={labels.renameConversationConfirm}
          cancelLabel={labels.renameConversationCancel}
          ariaLabel={labels.renameConversation}
          onSave={onRename}
          onCancel={onCancelEdit}
        />
      </div>
    );
  }

  return (
    <div className={`group/item relative ${indentClass}`}>
      <EstimationNavigationItem
        index={index}
        label={conversation.displayTitle}
        navigateTo={conversation.navigateTo}
        disabled={false}
        isOmitted={false}
        ignoreNumbering={true}
        hasChildren={false}
        isActive={conversation.isActive}
        onItemClick={onItemClick}
      />
      {/* EWM-534: conversations shared with the current user are read-only — show a badge instead
          of the owner-only rename / move / delete controls. */}
      {isSidebarOpen && !conversation.isOwner && (
        <div className='absolute top-1/2 right-2 z-10 -translate-y-1/2'>
          <span
            className='rounded bg-gray-500/15 px-1.5 py-0.5 text-[10px] font-medium tracking-wide text-gray-400 uppercase'
            title={labels.sharedBadgeTitle}
          >
            {labels.sharedBadge}
          </span>
        </div>
      )}
      {isSidebarOpen && conversation.isOwner && (
        <div className='absolute top-1/2 right-2 z-10 flex -translate-y-1/2 items-center gap-1 opacity-0 transition-opacity duration-200 group-hover/item:opacity-100 focus-within:opacity-100'>
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onEnterEditMode();
            }}
            className='rounded p-1 text-gray-400 hover:bg-blue-500/20 hover:text-blue-500 focus:opacity-100'
            title={labels.renameConversation}
            type='button'
          >
            <IconPencil className='h-3.5 w-3.5' />
          </button>
          <button
            ref={setFolderButtonEl}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onOpenPicker();
            }}
            className='rounded p-1 text-gray-400 hover:bg-blue-500/20 hover:text-blue-500 focus:opacity-100'
            title={labels.moveToFolder}
            type='button'
            aria-haspopup='listbox'
            aria-expanded={isPickerOpen}
          >
            <IconFolder className='h-3.5 w-3.5' />
          </button>
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onDelete();
            }}
            className='rounded p-1 text-gray-400 hover:bg-red-500/20 hover:text-red-500 focus:opacity-100'
            title={labels.deleteConversation}
            type='button'
          >
            <IconTrash className='h-3.5 w-3.5' />
          </button>
        </div>
      )}
      {isPickerOpen && (
        <FolderPicker
          folders={folders}
          currentFolderId={conversation.folderId}
          noFolderLabel={labels.moveToFolderNoFolderOption}
          createNewLabel={labels.moveToFolderCreateNew}
          maxFolderNameLength={MAX_FOLDER_NAME_LENGTH}
          createSaveLabel={labels.renameFolderConfirm}
          createCancelLabel={labels.renameFolderCancel}
          createAriaLabel={labels.renameFolder}
          anchorEl={folderButtonEl}
          onPickExisting={async (folderId) => {
            await onMoveToFolder(folderId);
            onClosePicker();
          }}
          onCreateAndPick={async (name) => {
            await onCreateFolderAndMove(name);
            onClosePicker();
          }}
          onClose={onClosePicker}
        />
      )}
    </div>
  );
};

interface IFolderRowProps {
  folder: ConversationFolderItem;
  isExpanded: boolean;
  isSidebarOpen: boolean;
  isEditing: boolean;
  onToggleExpanded: () => void;
  /** EWM-521 §5.5: expand (never collapse) when a drag hovers the collapsed folder header. */
  onAutoExpand: () => void;
  onEnterEditMode: () => void;
  onCancelEdit: () => void;
  onRename: (newName: string) => Promise<void>;
  onDelete: () => void;
  /** Children rendered when expanded — the conversation rows in this folder. */
  children?: React.ReactNode;
  labels: {
    renameFolder: string;
    renameFolderConfirm: string;
    renameFolderCancel: string;
    deleteFolder: string;
  };
}

const AUTO_EXPAND_HOVER_MS = 500;

const FolderRow: React.FC<IFolderRowProps> = ({
  folder,
  isExpanded,
  isSidebarOpen,
  isEditing,
  onToggleExpanded,
  onAutoExpand,
  onEnterEditMode,
  onCancelEdit,
  onRename,
  onDelete,
  children,
  labels,
}) => {
  // EWM-521 §5.5: a collapsed folder renders no drop zone, so when a drag hovers its header we
  // expand after a short dwell to reveal the list underneath. SortableJS uses native HTML5 DnD
  // by default, so the conversation drag fires real dragenter/dragleave events on this header.
  const autoExpandTimerRef = useRef<number | null>(null);
  const clearAutoExpandTimer = () => {
    if (autoExpandTimerRef.current !== null) {
      window.clearTimeout(autoExpandTimerRef.current);
      autoExpandTimerRef.current = null;
    }
  };
  const handleDragEnter = () => {
    if (isExpanded || autoExpandTimerRef.current !== null) return;
    autoExpandTimerRef.current = window.setTimeout(() => {
      autoExpandTimerRef.current = null;
      onAutoExpand();
    }, AUTO_EXPAND_HOVER_MS);
  };
  useEffect(() => clearAutoExpandTimer, []);

  return (
    <>
      <div
        className='group/item relative pl-4'
        onDragEnter={handleDragEnter}
        onDragLeave={clearAutoExpandTimer}
        onDrop={clearAutoExpandTimer}
      >
        {isEditing ? (
          <div className='pl-4'>
            <InlineRenameInput
              initialValue={folder.name}
              maxLength={MAX_FOLDER_NAME_LENGTH}
              saveLabel={labels.renameFolderConfirm}
              cancelLabel={labels.renameFolderCancel}
              ariaLabel={labels.renameFolder}
              onSave={async (name) => {
                await onRename(name);
              }}
              onCancel={onCancelEdit}
            />
          </div>
        ) : (
          <>
            <button
              type='button'
              onClick={onToggleExpanded}
              className='flex w-full items-center gap-2 rounded-md px-3 py-[6px] text-left text-sm font-semibold text-(--text-secondary) hover:bg-(--nav-item-bg)'
              aria-expanded={isExpanded}
            >
              <IconCaretDown
                className={`h-3 w-3 shrink-0 transition-transform duration-150 ${
                  isExpanded ? 'rotate-0' : '-rotate-90'
                }`}
              />
              <IconFolder className='text-c1 h-4 w-4 shrink-0' />
              <span className='flex-1 truncate'>{folder.name}</span>
              {!isExpanded && folder.conversationCount > 0 && (
                <span className='shrink-0 text-xs text-gray-400'>({folder.conversationCount})</span>
              )}
            </button>
            {isSidebarOpen && (
              <div className='absolute top-1/2 right-2 z-10 flex -translate-y-1/2 items-center gap-1 opacity-0 transition-opacity duration-200 group-hover/item:opacity-100 focus-within:opacity-100'>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onEnterEditMode();
                  }}
                  className='rounded p-1 text-gray-400 hover:bg-blue-500/20 hover:text-blue-500 focus:opacity-100'
                  title={labels.renameFolder}
                  type='button'
                >
                  <IconPencil className='h-3.5 w-3.5' />
                </button>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onDelete();
                  }}
                  className='rounded p-1 text-gray-400 hover:bg-red-500/20 hover:text-red-500 focus:opacity-100'
                  title={labels.deleteFolder}
                  type='button'
                >
                  <IconTrash className='h-3.5 w-3.5' />
                </button>
              </div>
            )}
          </>
        )}
      </div>
      {isExpanded && children}
    </>
  );
};

const AiAdvisorySidebar: React.FC<IAiAdvisorySidebarProps> = ({ isSidebarOpen, onItemClick }) => {
  const sidebarMessages = useMessages(SidebarMessages);
  const aiAdvisoryMsg = useMessages(aiAdvisoryMessages);
  const location = useLocation();
  const navigate = useNavigate();
  const {
    useGetConversations,
    useDeleteConversation,
    useRenameConversation,
    useGetConversationFolders,
    useCreateConversationFolder,
    useRenameConversationFolder,
    useDeleteConversationFolder,
    useMoveConversationToFolder,
  } = useAiAdvisoryQueryActions();

  const { isExpanded, toggle, expand } = useExpandedFoldersState();

  // Edit / dialog state
  const [isDeleteConversationModalOpen, setIsDeleteConversationModalOpen] = useState(false);
  const [conversationToDelete, setConversationToDelete] = useState<string | null>(null);
  const [folderToDelete, setFolderToDelete] = useState<ConversationFolderItem | null>(null);
  const [editingFolderId, setEditingFolderId] = useState<string | null>(null);
  const [editingConversationId, setEditingConversationId] = useState<string | null>(null);
  const [pickerOpenForConversationId, setPickerOpenForConversationId] = useState<string | null>(
    null,
  );

  // EWM-521 §5.5: local mirrors of the grouped lists so ReactSortable can run as a controlled
  // list. They are seeded from the server-derived groups below; a cross-list drop fires the move
  // mutation, whose refetch re-derives the groups and re-syncs these mirrors.
  const [draggableUnfoldered, setDraggableUnfoldered] = useState<IConversationDisplay[]>([]);
  const [draggableByFolder, setDraggableByFolder] = useState<Map<string, IConversationDisplay[]>>(
    new Map(),
  );

  const { data: conversationsData, isLoading: isLoadingConversations } = useGetConversations() as {
    data: GetConversationsResponse | undefined;
    isLoading: boolean;
  };

  const { data: foldersData, isLoading: isLoadingFolders } = useGetConversationFolders() as {
    data: GetConversationFoldersResponse | undefined;
    isLoading: boolean;
  };

  const deleteConversationMutation = useDeleteConversation();
  const renameConversationMutation = useRenameConversation();
  const createFolderMutation = useCreateConversationFolder();
  const renameFolderMutation = useRenameConversationFolder();
  const deleteFolderMutation = useDeleteConversationFolder();
  const moveConversationMutation = useMoveConversationToFolder();

  const isActive = location.href.includes('/ai-advisory');
  const searchParams = new URLSearchParams(location.search);
  const currentConversationId = searchParams.get('conversationId') || undefined;

  const folders = foldersData?.folders ?? [];

  const conversations: IConversationDisplay[] = useMemo(() => {
    if (!conversationsData?.conversations || isLoadingConversations) return [];

    // Map every conversation to its display shape. Empty / repetitive titles are handled by
    // buildDisplayTitle (date-label fallback), so no conversation is hidden here — dedupe now
    // happens per list during grouping below (EWM-860).
    return conversationsData.conversations.map((c: ConversationItemType) => ({
      id: c.conversationId,
      conversationId: c.conversationId,
      rawTitle: c.title,
      displayTitle: buildDisplayTitle(c.title, c.createdAt),
      folderId: c.folderId ?? null,
      isActive: currentConversationId === c.conversationId,
      navigateTo: `/ai-advisory?conversationId=${c.conversationId}`,
      // Default to owner when the field is absent so older API responses keep full controls.
      isOwner: c.isOwner ?? true,
    }));
  }, [conversationsData, isLoadingConversations, currentConversationId]);

  // Group conversations by folder + collect unfoldered for the bottom of the list. Dedupe is
  // scoped per list (EWM-860) so a recent unfoldered conversation can't suppress an older
  // foldered one with a colliding title — which previously left folders under-rendered vs. their
  // count badge.
  const { conversationsByFolder, unfolderedConversations } = useMemo(() => {
    const { byFolder, unfoldered } = groupConversationsByFolder(conversations);
    return { conversationsByFolder: byFolder, unfolderedConversations: unfoldered };
  }, [conversations]);

  // Keep the draggable mirrors in step with the server-derived groups (initial load + post-move
  // refetch). Within-list order is server-controlled (§5.1), so we only mirror, never persist order.
  // Synced during render rather than in an effect so the re-sync lands before paint (no flash after
  // a move's refetch); see https://react.dev/learn/you-might-not-need-an-effect.
  // EWM-936: the previous-value marker is state, not a ref. Writing a ref during render
  // is not allowed, whereas storing the previous prop in state and adjusting during render
  // is the documented form of this exact pattern, and keeps the same before-paint timing.
  const [lastSyncedGroups, setLastSyncedGroups] = useState<{
    unfoldered: IConversationDisplay[];
    byFolder: Map<string, IConversationDisplay[]>;
  } | null>(null);
  if (
    lastSyncedGroups?.unfoldered !== unfolderedConversations ||
    lastSyncedGroups?.byFolder !== conversationsByFolder
  ) {
    setLastSyncedGroups({
      unfoldered: unfolderedConversations,
      byFolder: conversationsByFolder,
    });
    setDraggableUnfoldered(unfolderedConversations);
    setDraggableByFolder(conversationsByFolder);
  }

  const conversationLabels = {
    moveToFolder: aiAdvisoryMsg.moveToFolder(),
    deleteConversation: aiAdvisoryMsg.deleteConversation(),
    moveToFolderNoFolderOption: aiAdvisoryMsg.moveToFolderNoFolderOption(),
    moveToFolderCreateNew: aiAdvisoryMsg.moveToFolderCreateNew(),
    renameFolder: aiAdvisoryMsg.renameFolder(),
    renameFolderConfirm: aiAdvisoryMsg.renameFolderConfirm(),
    renameFolderCancel: aiAdvisoryMsg.renameFolderCancel(),
    renameConversation: aiAdvisoryMsg.renameConversation(),
    renameConversationConfirm: aiAdvisoryMsg.renameConversationConfirm(),
    renameConversationCancel: aiAdvisoryMsg.renameConversationCancel(),
    sharedBadge: aiAdvisoryMsg.sharedBadge(),
    sharedBadgeTitle: aiAdvisoryMsg.sharedBadgeTitle(),
  };
  const folderLabels = {
    renameFolder: aiAdvisoryMsg.renameFolder(),
    renameFolderConfirm: aiAdvisoryMsg.renameFolderConfirm(),
    renameFolderCancel: aiAdvisoryMsg.renameFolderCancel(),
    deleteFolder: aiAdvisoryMsg.deleteFolder(),
  };

  const handleMoveConversation = async (conversationId: string, folderId: string | null) => {
    try {
      await moveConversationMutation.mutateAsync({ conversationId, folderId });
    } catch (err) {
      console.error('Failed to move conversation:', err);
      // eslint-disable-next-line no-alert
      alert(
        `${aiAdvisoryMsg.moveToFolderError()}: ${err instanceof Error ? err.message : 'Unknown error'}`,
      );
    }
  };

  // EWM-521 §5.5: ReactSortable setList handler for a single list (a folder, or null = unfoldered).
  // Fires the move mutation for any conversation that arrived here from another list. ReactSortable's
  // setList double-fires (EWM-569) on plain mouse-down/up, but those carry no folder change, so the
  // `folderId` diff naturally guards against spurious mutations and within-list reorders.
  const handleSortableChange = (targetFolderId: string | null, newList: IConversationDisplay[]) => {
    // Single pass: collect the conversations that arrived from another list and, in the same map,
    // patch their folderId on the local mirror so a redundant setList for the same drop won't re-fire.
    const movedIds: string[] = [];
    const patched = newList.map((c) => {
      if ((c.folderId ?? null) === targetFolderId) return c;
      movedIds.push(c.conversationId);
      return { ...c, folderId: targetFolderId };
    });
    if (targetFolderId === null) {
      setDraggableUnfoldered(patched);
    } else {
      setDraggableByFolder((prev) => {
        const next = new Map(prev);
        next.set(targetFolderId, patched);
        return next;
      });
    }
    movedIds.forEach((id) => void handleMoveConversation(id, targetFolderId));
  };

  const handleCreateFolderAndMove = async (conversationId: string, name: string) => {
    try {
      const created = await createFolderMutation.mutateAsync({ name });
      await moveConversationMutation.mutateAsync({
        conversationId,
        folderId: created.folderId,
      });
    } catch (err) {
      console.error('Failed to create folder + move conversation:', err);
      // eslint-disable-next-line no-alert
      alert(
        `${aiAdvisoryMsg.createFolderError()}: ${err instanceof Error ? err.message : 'Unknown error'}`,
      );
    }
  };

  const handleCreateEmptyFolder = async () => {
    try {
      const created = await createFolderMutation.mutateAsync({
        name: aiAdvisoryMsg.newFolderDefaultName(),
      });
      // Per EWM-521 §5.2, immediately enter rename mode on the new folder.
      setEditingFolderId(created.folderId);
    } catch (err) {
      console.error('Failed to create folder:', err);
      // eslint-disable-next-line no-alert
      alert(
        `${aiAdvisoryMsg.createFolderError()}: ${err instanceof Error ? err.message : 'Unknown error'}`,
      );
    }
  };

  const handleRenameFolder = async (folderId: string, name: string) => {
    try {
      await renameFolderMutation.mutateAsync({ folderId, name });
      setEditingFolderId(null);
    } catch (err) {
      console.error('Failed to rename folder:', err);
      // eslint-disable-next-line no-alert
      alert(
        `${aiAdvisoryMsg.renameFolderError()}: ${err instanceof Error ? err.message : 'Unknown error'}`,
      );
    }
  };

  // EWM-520: Rename a conversation. Server enforces owner-only + 1–100 char title.
  const handleRenameConversation = async (conversationId: string, title: string) => {
    try {
      await renameConversationMutation.mutateAsync({ conversationId, title });
      setEditingConversationId(null);
    } catch (err) {
      console.error('Failed to rename conversation:', err);
      // eslint-disable-next-line no-alert
      alert(
        `${aiAdvisoryMsg.renameConversationError()}: ${err instanceof Error ? err.message : 'Unknown error'}`,
      );
    }
  };

  const renderConversationRow = (
    conversation: IConversationDisplay,
    index: number,
    isInFolder = false,
  ) => (
    // Wrap in a plain DOM node so ReactSortable's cloned data-id / sortable-* classes land on a
    // real element (it clones props onto the direct child) — matches the repo's intrinsic-child
    // usage and keeps this row the draggable unit.
    <div key={conversation.conversationId}>
      <ConversationRow
        conversation={conversation}
        index={index}
        isSidebarOpen={isSidebarOpen}
        isInFolder={isInFolder}
        folders={folders}
        isPickerOpen={pickerOpenForConversationId === conversation.conversationId}
        isEditing={editingConversationId === conversation.conversationId}
        onItemClick={onItemClick}
        onOpenPicker={() => setPickerOpenForConversationId(conversation.conversationId)}
        onClosePicker={() => setPickerOpenForConversationId(null)}
        onMoveToFolder={(folderId) => handleMoveConversation(conversation.conversationId, folderId)}
        onCreateFolderAndMove={(name) =>
          handleCreateFolderAndMove(conversation.conversationId, name)
        }
        onDelete={() => {
          setConversationToDelete(conversation.conversationId);
          setIsDeleteConversationModalOpen(true);
        }}
        onEnterEditMode={() => setEditingConversationId(conversation.conversationId)}
        onCancelEdit={() => setEditingConversationId(null)}
        onRename={(newTitle) => handleRenameConversation(conversation.conversationId, newTitle)}
        labels={conversationLabels}
      />
    </div>
  );

  if (!isActive) {
    return (
      <Link to='/ai-advisory' activeOptions={{ exact: false }} onClick={onItemClick}>
        <div className='w-full'>
          <div className='flex gap-2'>
            <div className='h-6 w-6'>
              <IconBot className='text-c1' />
            </div>
            <span
              className={`font-semibold transition-opacity duration-200 ${isSidebarOpen ? 'opacity-100' : 'opacity-0'} whitespace-nowrap`}
            >
              {sidebarMessages.aiAdvisory()}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <>
      <Link to='/ai-advisory' activeOptions={{ exact: false }} onClick={onItemClick}>
        <div className='w-full'>
          <div className='flex gap-2'>
            <div className='h-6 w-6'>
              <IconBot className='text-c1' />
            </div>
            <span
              className={`font-semibold transition-opacity duration-200 ${isSidebarOpen ? 'opacity-100' : 'opacity-0'} whitespace-nowrap`}
            >
              {sidebarMessages.aiAdvisory()}
            </span>
          </div>
        </div>
      </Link>
      <div
        className={`${
          isSidebarOpen ? 'max-h-[1500px]' : 'max-h-0'
        } grid w-full overflow-hidden transition-all duration-300 ease-in-out`}
      >
        <div className='gap-1 first:mt-5'>
          {(isLoadingConversations || isLoadingFolders) && (
            <div className='px-3 py-2 text-sm text-gray-500'>Loading conversations...</div>
          )}

          {/* Folders — alphabetical, above unfoldered conversations (EWM-521 §5.1). */}
          {!isLoadingFolders &&
            folders.map((folder) => {
              const expanded = isExpanded(folder.folderId);
              const folderConversations = draggableByFolder.get(folder.folderId) ?? [];
              return (
                <FolderRow
                  key={folder.folderId}
                  folder={folder}
                  isExpanded={expanded}
                  isSidebarOpen={isSidebarOpen}
                  isEditing={editingFolderId === folder.folderId}
                  onToggleExpanded={() => toggle(folder.folderId)}
                  onAutoExpand={() => expand(folder.folderId)}
                  onEnterEditMode={() => setEditingFolderId(folder.folderId)}
                  onCancelEdit={() => setEditingFolderId(null)}
                  onRename={(name) => handleRenameFolder(folder.folderId, name)}
                  onDelete={() => setFolderToDelete(folder)}
                  labels={folderLabels}
                >
                  {/* min-height keeps an empty (expanded) folder a reachable drop target. */}
                  <ReactSortable<IConversationDisplay>
                    list={folderConversations}
                    setList={(newList: IConversationDisplay[]) =>
                      handleSortableChange(folder.folderId, newList)
                    }
                    group={CONVERSATION_SORTABLE_GROUP}
                    animation={200}
                    className='min-h-[28px]'
                  >
                    {folderConversations.map((c, idx) => renderConversationRow(c, idx, true))}
                  </ReactSortable>
                </FolderRow>
              );
            })}

          {/* Unfoldered conversations — bottom of the list, sorted by recency
              (server already sorts by LastMessageAt desc). */}
          {!isLoadingConversations &&
            unfolderedConversations.length === 0 &&
            folders.length === 0 && (
              <div className='px-3 py-2 text-sm text-gray-500'>No conversations yet</div>
            )}
          <ReactSortable<IConversationDisplay>
            list={draggableUnfoldered}
            setList={(newList: IConversationDisplay[]) => handleSortableChange(null, newList)}
            group={CONVERSATION_SORTABLE_GROUP}
            animation={200}
            className='min-h-[28px]'
          >
            {draggableUnfoldered.map((c, idx) => renderConversationRow(c, idx))}
          </ReactSortable>

          {/* Action buttons (new folder + new conversation) */}
          <div className='mt-2 flex flex-col gap-1 pl-8'>
            <button
              type='button'
              onClick={() => void handleCreateEmptyFolder()}
              disabled={createFolderMutation.isPending}
              className='flex w-full items-center gap-2 rounded-md px-3 py-[6px] text-left text-sm font-semibold text-(--text-secondary) hover:bg-(--nav-item-bg) disabled:cursor-not-allowed disabled:opacity-50'
            >
              <IconFolderPlus className='h-4 w-4 shrink-0' />
              <span>{aiAdvisoryMsg.newFolder()}</span>
            </button>
            <Link
              to='/ai-advisory'
              search={{}}
              onClick={onItemClick}
              className={`${!currentConversationId ? 'bg-(--color-light-grey) text-(--primary) dark:bg-(--nav-item-bg)' : 'text-(--text-secondary)'} flex w-full cursor-pointer items-center gap-2 rounded-md px-3 py-[6px] text-sm font-semibold hover:bg-(--color-light-grey) dark:hover:bg-(--nav-item-bg)`}
            >
              <IconPlusCircle duotone={false} className='h-4 w-4 shrink-0' />
              <span>{sidebarMessages.newConversation()}</span>
            </Link>
          </div>
        </div>
      </div>

      {/* Delete conversation confirmation */}
      <ConfirmationModal
        open={isDeleteConversationModalOpen}
        isConfirmProcessing={deleteConversationMutation.isPending}
        onClose={() => {
          setIsDeleteConversationModalOpen(false);
          setConversationToDelete(null);
        }}
        title={aiAdvisoryMsg.deleteConversationTitle()}
        warningMessage={aiAdvisoryMsg.deleteConversationWarning()}
        confirmButtonLabel={aiAdvisoryMsg.deleteConversationConfirm()}
        onConfirm={async () => {
          if (!conversationToDelete) return;
          try {
            await deleteConversationMutation.mutateAsync(conversationToDelete);
            if (conversationToDelete === currentConversationId) {
              void navigate({ to: '/ai-advisory', search: {} });
            }
          } catch (error) {
            console.error('Failed to delete conversation:', error);
            // eslint-disable-next-line no-alert
            alert(
              `Failed to delete conversation: ${error instanceof Error ? error.message : 'Unknown error'}`,
            );
          } finally {
            setIsDeleteConversationModalOpen(false);
            setConversationToDelete(null);
          }
        }}
      />

      {/* Delete folder confirmation — message varies based on whether folder has contents (EWM-521 §5.6) */}
      <ConfirmationModal
        open={folderToDelete !== null}
        isConfirmProcessing={deleteFolderMutation.isPending}
        onClose={() => setFolderToDelete(null)}
        title={aiAdvisoryMsg.deleteFolderTitle()}
        warningMessage={
          folderToDelete
            ? folderToDelete.conversationCount > 0
              ? aiAdvisoryMsg.deleteFolderWarningWithContents({
                  name: folderToDelete.name,
                  // useMessages typing requires string values; ICU plural format still works on the
                  // numeric string at runtime.
                  count: String(folderToDelete.conversationCount),
                })
              : aiAdvisoryMsg.deleteFolderWarningEmpty({ name: folderToDelete.name })
            : ''
        }
        confirmButtonLabel={aiAdvisoryMsg.deleteFolderConfirm()}
        onConfirm={async () => {
          if (!folderToDelete) return;
          try {
            await deleteFolderMutation.mutateAsync(folderToDelete.folderId);
          } catch (error) {
            console.error('Failed to delete folder:', error);
            // eslint-disable-next-line no-alert
            alert(
              `${aiAdvisoryMsg.deleteFolder()}: ${error instanceof Error ? error.message : 'Unknown error'}`,
            );
          } finally {
            setFolderToDelete(null);
          }
        }}
      />
    </>
  );
};

export default AiAdvisorySidebar;
